import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import AuthGuard from "./AuthGuard";
import GuestGuard from "./GuestGuard";
import AppErrorFallback from "../components/ErrorBoundary/AppErrorFallback";

// Layouts
import AuthLayout from "../components/layouts/AuthLayout";
const DashboardLayout = lazy(
  () => import("../components/layouts/DashboardLayout"),
);

// Preserves ?redirect=... (set by AuthGuard) while bouncing /auth's index to /auth/request-otp
const AuthIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/auth/request-otp${search}`} replace />;
};

const None = lazy(() => import("../components/pages/none"));

// Auth
const Login = lazy(() => import("../components/pages/auth/login"));
const Verification = lazy(
  () => import("../components/pages/auth/verification"),
);
const ResetOrCreatePass = lazy(
  () => import("../components/pages/auth/resetOrCreatePass"),
);
const ForgotPassword = lazy(
  () => import("../components/pages/auth/forgotPassword"),
);
const LoginWithOtp = lazy(
  () => import("../components/pages/auth/loginWithOtp"),
);

const Home = lazy(() => import("../components/pages/dashboard/home"));
const Settings = lazy(() => import("../components/pages/dashboard/settings"));
const UserManagement = lazy(
  () => import("../components/pages/dashboard/settings/UserManagement"),
);
const TeamManagement = lazy(
  () => import("../components/pages/dashboard/teamManagement"),
);
const EmployeeManagement = lazy(
  () => import("../components/pages/dashboard/employeeManagement"),
);
const Projects = lazy(() => import("../components/pages/dashboard/projects"));
const TaskManagement = lazy(
  () => import("../components/pages/dashboard/taskManagement"),
);
const TaskRedirect = lazy(
  () => import("../components/pages/dashboard/taskRedirect"),
);
const EmployeeTaskManagement = lazy(
  () => import("../components/pages/dashboard/employee/EmployeeTaskManagement"),
);
const EmployeeProjects = lazy(
  () =>
    import("../components/pages/dashboard/employee/EmployeeProjectManagement"),
);
const MyProfile = lazy(
  () => import("../components/pages/dashboard/my-profile"),
);
const ProjectTask = lazy(
  () => import("../components/pages/dashboard/ProjectTask"),
);
const Reports = lazy(() => import("../components/pages/dashboard/reports"));
const ClientManagement = lazy(() => import("../components/pages/dashboard/settings/ClientManagement"));
const EmployeeRates = lazy(() => import("../components/pages/dashboard/settings/EmployeeRates"));
const Notifications = lazy(() => import("../components/pages/dashboard/notificationsPage"));
const DailyStatusReport = lazy(() => import("../components/pages/dashboard/dailyStatusReport"));
const StatusReportPage = lazy(() => import("../components/pages/dashboard/StatusReportPage"));
const TeamStatusReport = lazy(() => import("../components/pages/dashboard/TeamStatusReport"));
const ClientDashboard = lazy(() => import("../components/pages/dashboard/ClientDashboard"));
const MyTimesheet = lazy(() => import("../components/pages/dashboard/timesheet"));
// const TeamTimesheet = lazy(() => import("../components/pages/dashboard/timesheet/TeamTimesheet"));
const ProtectedRoute = lazy(() => import("../components/ProtectedRoute/index"));
const CRSettings = lazy(
  () => import("../components/pages/dashboard/changeRequests/CRSettings"),
);
const CostAndBilling = lazy(
  () => import("../components/pages/dashboard/billing"),
);
const ProjectBillingDetail = lazy(
  () => import("../components/pages/dashboard/billing/ProjectBillingDetail"),
);
// Dashboard

const routes = [
  {
    path: "/",
    element: <Navigate to="/auth" />,
  },
  {
    path: "auth",
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: [
      {
        index: true, // Make this the index route
        element: <AuthIndexRedirect />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "verify",
        element: <Verification />,
      },
      {
        path: "request-otp",
        element: <LoginWithOtp />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password",
        element: <ResetOrCreatePass type="reset" />,
      },
      {
        path: "create-password",
        element: <ResetOrCreatePass type="create" />,
      },
    ],
  },
  {
    path: "dashboard/*",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
  },
  {
    // Root-level so it matches the verified App Links path for the mobile app
    // (host + /task/:id, no /dashboard prefix). Resolves to the correct
    // dashboard module (my-tasks vs tasks) and redirects there.
    path: "task/:taskId",
    element: (
      <AuthGuard>
        <TaskRedirect />
      </AuthGuard>
    ),
  },
  {
    path: "employee/dashboard/*",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
  },
  {
    path: "none",
    element: <None />,
  },
  {
    path: "test/error-boundary",
    element: <AppErrorFallback />,
  },
  {
    path: "*",
    element: <Navigate to="/dashboard?view=table" replace />,
  },
];

export const dashboardRoute = (
  <Suspense>
    <Routes>
      {/*  " " Always accessible */}
      <Route path="/" element={<Home />} />

      {/*  " " Requires "Client Dashboard" module */}
      <Route
        path="client-report"
        element={
          <ProtectedRoute requiredModules={["Client Dashboard"]}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires ANY of: Role Management, Department, Notifications */}
      <Route
        path="settings"
        element={
          <ProtectedRoute
            requiredModules={["Role Management", "Department", "Notifications"]}
          >
            <Settings />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Employee Management" */}
      <Route
        path="employees"
        element={
          <ProtectedRoute requiredModules={["Employee Management"]}>
            <EmployeeManagement />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Team Management" */}
      <Route
        path="teams"
        element={
          <ProtectedRoute requiredModules={["Team Management"]}>
            <TeamManagement />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Projects" */}
      <Route
        path="projects"
        element={
          <ProtectedRoute requiredModules={["Projects"]}>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="clientmanagement"
        element={
          <ProtectedRoute requiredModules={["Client Management"]}>
            <ClientManagement />
          </ProtectedRoute>
        }
      />

      {/* ✅ Employee Rates */}
      <Route
        path="employee-rates"
        element={
          <ProtectedRoute requiredModules={["Employee Rates"]}>
            <EmployeeRates />
          </ProtectedRoute>
        }
      />
      <Route
        path="projects/projectinfo/:id"
        element={
          <ProtectedRoute requiredModules={["Projects"]}>
            <ProjectTask />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Tasks" */}
      <Route
        path="tasks"
        element={
          <ProtectedRoute requiredModules={["Tasks"]}>
            <TaskManagement key="tasks" />
          </ProtectedRoute>
        }
      />

      <Route
        path="my-tasks"
        element={
          <ProtectedRoute requiredModules={["Tasks"]}>
            <TaskManagement key="my-tasks" mytask={true} />
          </ProtectedRoute>
        }
      />

      {/*  " " Always accessible */}
      <Route path="my-profile" element={<MyProfile />} />

      {/*  " " Requires "Reports" */}
      <Route
        path="reports"
        element={
          <ProtectedRoute requiredModules={["Reports"]}>
            <Reports />
          </ProtectedRoute>
        }
      />

      <Route
        path="daily-status-report"
        element={
          <ProtectedRoute requiredModules={["Reports"]}>
            <DailyStatusReport />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Team DSR Report" */}
      <Route
        path="team-status-report"
        element={
          <ProtectedRoute requiredModules={["Team DSR Report"]}>
            <TeamStatusReport />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Notifications" */}
      <Route
        path="notifications"
        element={
          <ProtectedRoute requiredModules={["Notifications"]}>
            <Notifications />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Reports" — My Timesheet */}
      <Route
        path="my-timesheet"
        element={
          <ProtectedRoute requiredModules={["Reports"]}>
            <MyTimesheet />
          </ProtectedRoute>
        }
      />

      {/*  " " Requires "Team DSR Report" — Team Timesheet */}
      {/* <Route
        path="team-timesheet"
        element={
          <ProtectedRoute requiredModules={["Team DSR Report"]}>
            <TeamTimesheet />
          </ProtectedRoute>
        }
      /> */}

      
      <Route
        path="change-requests/settings"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={[
              "CR Versions",
              "CR Labels",
              "CR Departments",
              "CR Environments",
              "CR Projects",
              "CR Services",
              "CR Teams",
            ]}
          >
            <CRSettings />
          </ProtectedRoute>
        }
      />

      {/*  " " Change Requests — Form Templates */}
      <Route
        path="change-requests/form-templates"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={["Form Templates"]}
          >
            <CRSettings />
          </ProtectedRoute>
        }
      />

      {/*  " " Change Requests — Approval Chains */}
      <Route
        path="change-requests/approval-chains"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={["Approval Chains"]}
          >
            <CRSettings />
          </ProtectedRoute>
        }
      />
       <Route
        path="change-requests/all-requests"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={["All Requests"]}
          >
            <CRSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="change-requests/timeline"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={["Timeline"]}
          >
            <CRSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="change-requests/analytics"
        element={
          <ProtectedRoute
            requiresBusiness="change_management"
            requiredModules={["Analytics"]}
          >
            <CRSettings />
          </ProtectedRoute>
        }/>

      {/* ── Billing ── */}
      <Route path="billing" element={
        <ProtectedRoute requiresBusiness="cost_and_billing" requiredModules={["Cost & Billing"]}>
          <CostAndBilling />
        </ProtectedRoute>
      } />
      <Route path="billing/reports" element={
        <ProtectedRoute requiresBusiness="cost_and_billing" requiredModules={["Cost & Billing"]}>
          <CostAndBilling />
        </ProtectedRoute>
      } />
      <Route
        path="billing/project/:projectId"
        element={
          <ProtectedRoute requiresBusiness="cost_and_billing" requiredModules={["Cost & Billing"]}>
            <ProjectBillingDetail />
          </ProtectedRoute>
        }
      />
    </Routes>
  </Suspense>
);

export const employeeDashboardRoute = (
  <Suspense>
    <Routes>
      <Route path="tasks" element={<TaskManagement />}></Route>
    </Routes>
    <Routes>
      <Route path="projects" element={<EmployeeProjects />}></Route>
    </Routes>
    <Routes>
      <Route path="my-profile" element={<MyProfile />}></Route>
    </Routes>
  </Suspense>
);

export default routes;
